import { integrationTesting } from '@ps/web-core-utils';
import { useNavigate } from '@ps/web-shared-components';

import { Fork, Invoke, nextPointer } from '@swf/components/StarsWizard/Flow';
import useWizardExit from '@swf/core/hooks/useWizardExit';
import type { FlowConfig } from '@swf/core/types/Wizard';
import { Action } from '@swf/core/types/Wizard';

import SignupBlockedCountry from '../components/SignupBlockedCountry/SignupBlockedCountry';
import SignupCountry from '../components/SignupCountry/SignupCountry';
import SignupEmail from '../components/SignupEmail/SignupEmail';
import SignupLegal from '../components/SignupLegal/SignupLegal';
import SignupPIN from '../components/SignupPIN/SignupPIN';
import SignupPassword from '../components/SignupPassword/SignupPassword';
import SignupUsername from '../components/SignupUsername/SignupUsername';

import { Guard } from './useSignupGuards';

const successDelay = integrationTesting ? 0 : 3000;
const signupRegisterDelay = {
  name: 'signupRegisterDelay',
  after: { [successDelay]: nextPointer }
};
const useSignUpBlackListFlow = (): FlowConfig => {
  const navigate = useNavigate();
  const exitWizard = useWizardExit();

  const forkCountryAvailable = new Fork(
    Guard.SignupCountryAvailable,
    [SignupUsername],
    [SignupBlockedCountry]
  );

  const invokeSignupCountryCheck = new Invoke(
    'signupCountryCheck',
    forkCountryAvailable,
    SignupCountry
  );

  const forkCompleteSignup = new Fork(
    Guard.AutoTrigerRmf,
    [{ name: 'enterRMF', entry: () => navigate('/account/cashier/rmf'), type: 'final' }],
    []
  );

  const forkRmfcheck = new Fork(
    Guard.SignupRmfEnabled,
    [{ name: 'signupExit', entry: Action.GoNext }],
    [forkCompleteSignup]
  );

  const invokeSignupRegister = new Invoke('signupRegister', signupRegisterDelay, SignupLegal);

  const invokeSetCountry = new Invoke('setCountry', invokeSignupCountryCheck);

  const forkSkipSignupCountry = new Fork(
    Guard.SkipSignupCountry,
    [invokeSetCountry],
    [SignupCountry]
  );

  const forkAuthenticationCheck = new Fork(
    Guard.AuthenticationCheck,
    [{ name: 'exitSignup', entry: exitWizard, type: 'final' }],
    [forkSkipSignupCountry, SignupCountry]
  );

  return [
    forkAuthenticationCheck,
    invokeSignupCountryCheck,
    forkCountryAvailable,
    SignupPassword,
    SignupEmail,
    SignupPIN,
    SignupLegal,
    invokeSignupRegister,
    signupRegisterDelay,
    forkRmfcheck
  ];
};

export default useSignUpBlackListFlow;
